import { useRef, type KeyboardEvent } from 'react';
import { Check, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ChoiceTone = 'default' | 'warning' | 'destructive';
export type ChoiceLayout = 'segmented' | 'chips' | 'cards';
export type ChoiceSize = 'sm' | 'md';

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  hint?: string;
  icon?: LucideIcon;
  disabled?: boolean;
  dir?: 'ltr' | 'rtl';
  tone?: ChoiceTone;
}

export interface ChoiceButtonsProps<T extends string> {
  id?: string;
  name?: string;
  value: T | '' | null | undefined;
  onChange: (value: T) => void;
  onClear?: () => void;
  options: readonly ChoiceOption<T>[];
  layout?: ChoiceLayout;
  size?: ChoiceSize;
  inline?: boolean;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  columns?: 2 | 3 | 4;
  className?: string;
  onBlur?: () => void;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const PREVIOUS_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

const SEGMENT_TEXT: Record<ChoiceTone, string> = {
  default: 'text-foreground',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

const CHIP_SELECTED: Record<ChoiceTone, string> = {
  default: 'border-primary bg-primary/10 text-primary',
  warning: 'border-warning/50 bg-warning/10 text-warning',
  destructive: 'border-destructive/50 bg-destructive/10 text-destructive',
};

function isRtl(element: HTMLElement | null): boolean {
  return element !== null && getComputedStyle(element).direction === 'rtl';
}

function stepFor(event: KeyboardEvent<HTMLElement>, rtl: boolean): number {
  const forward = NEXT_KEYS.has(event.key);
  if (!forward && !PREVIOUS_KEYS.has(event.key)) return 0;
  const horizontal = event.key === 'ArrowLeft' || event.key === 'ArrowRight';
  return (forward ? 1 : -1) * (horizontal && rtl ? -1 : 1);
}

function groupClass(layout: ChoiceLayout, inline: boolean, columns: ChoiceButtonsProps<string>['columns']): string {
  if (layout === 'chips') return 'flex flex-wrap gap-1.5';
  if (layout === 'cards') {
    return cn('grid gap-2', columns === 3 ? 'sm:grid-cols-3' : columns === 4 ? 'grid-cols-2 sm:grid-cols-4' : 'sm:grid-cols-2');
  }
  return cn(
    'flex-wrap gap-0.5 rounded-lg border border-input bg-muted/50 p-0.5 dark:bg-muted/30',
    inline ? 'inline-flex max-w-full' : 'flex w-full',
  );
}

function optionClass(layout: ChoiceLayout, size: ChoiceSize, selected: boolean, tone: ChoiceTone): string {
  const small = size === 'sm';
  if (layout === 'chips') {
    return cn(
      'inline-flex items-center gap-1.5 rounded-full border font-medium',
      small ? 'min-h-7 px-2.5 py-0.5 text-xs' : 'min-h-8 px-3 py-1 text-sm',
      selected ? CHIP_SELECTED[tone] : 'border-input bg-background text-foreground hover:border-muted-foreground/40 hover:bg-muted/60',
    );
  }
  if (layout === 'cards') {
    return cn(
      'relative flex min-h-[3.25rem] items-start gap-2.5 rounded-lg border px-3 py-2.5 text-start text-sm',
      selected ? 'border-primary bg-primary/[0.06] ring-1 ring-primary/40' : 'border-input bg-background hover:border-muted-foreground/40 hover:bg-muted/40',
    );
  }
  return cn(
    'inline-flex min-w-0 items-center justify-center gap-1.5 rounded-md text-center font-medium leading-tight',
    small ? 'min-h-7 flex-[1_1_3rem] px-2 py-0.5 text-xs' : 'min-h-8 flex-[1_1_4rem] px-2.5 py-1 text-sm',
    selected ? cn('bg-background shadow-sm ring-1 ring-border dark:bg-card', SEGMENT_TEXT[tone]) : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
  );
}

export function ChoiceButtons<T extends string>({
  id,
  name,
  value,
  onChange,
  onClear,
  options,
  layout = 'segmented',
  size = 'md',
  inline = false,
  label,
  disabled,
  required,
  columns,
  className,
  onBlur,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: ChoiceButtonsProps<T>) {
  const group = useRef<HTMLDivElement>(null);
  const enabled = options.filter((option) => !option.disabled);
  const focusTarget = enabled.some((option) => option.value === value) ? value : enabled[0]?.value;
  const accessibleName = label ?? ariaLabel;

  const focusChoice = (choice: T) => {
    group.current?.querySelector<HTMLButtonElement>(`[data-choice="${CSS.escape(choice)}"]`)?.focus();
  };

  const move = (event: KeyboardEvent<HTMLButtonElement>, current: T) => {
    if (disabled || enabled.length === 0) return;
    let next: ChoiceOption<T> | undefined;
    if (event.key === 'Home') next = enabled[0];
    else if (event.key === 'End') next = enabled[enabled.length - 1];
    else {
      const step = stepFor(event, isRtl(group.current));
      if (step === 0) return;
      const index = enabled.findIndex((option) => option.value === current);
      next = enabled[(index + step + enabled.length) % enabled.length];
    }
    event.preventDefault();
    if (!next) return;
    onChange(next.value);
    focusChoice(next.value);
  };

  const pick = (choice: T) => {
    if (choice === value && onClear) onClear();
    else onChange(choice);
  };

  return (
    <div
      ref={group}
      id={id}
      role="radiogroup"
      aria-label={accessibleName}
      aria-labelledby={accessibleName ? undefined : (ariaLabelledBy ?? (id ? `${id}-label` : undefined))}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      aria-required={required || undefined}
      aria-disabled={disabled || undefined}
      onBlur={(event) => {
        if (onBlur && !event.currentTarget.contains(event.relatedTarget as Node | null)) onBlur();
      }}
      className={cn(groupClass(layout, inline, columns), invalid && layout === 'segmented' && 'border-destructive', className)}
    >
      <input type="hidden" data-field-value name={name} value={value ?? ''} />
      {options.map((option) => {
        const selected = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            data-choice={option.value}
            aria-checked={selected}
            tabIndex={option.value === focusTarget ? 0 : -1}
            disabled={disabled || option.disabled}
            title={layout === 'cards' ? undefined : option.hint}
            onClick={() => pick(option.value)}
            onKeyDown={(event) => move(event, option.value)}
            className={cn(
              'focus-ring transition-[color,background-color,border-color,box-shadow] duration-150 disabled:cursor-not-allowed disabled:opacity-50',
              optionClass(layout, size, selected, option.tone ?? 'default'),
              invalid && layout === 'chips' && !selected && 'border-destructive',
            )}
          >
            {Icon && (
              <Icon
                className={cn(
                  'shrink-0',
                  size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4',
                  layout === 'cards' && 'mt-0.5',
                  layout === 'cards' && (selected ? 'text-primary' : 'text-muted-foreground'),
                )}
                aria-hidden
              />
            )}
            {layout === 'cards' ? (
              <span className="min-w-0 flex-1">
                <span dir={option.dir} className="block font-medium leading-snug">
                  {option.label}
                </span>
                {option.hint && <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{option.hint}</span>}
              </span>
            ) : (
              <span dir={option.dir} className="min-w-0 [overflow-wrap:anywhere]">
                {option.label}
              </span>
            )}
            {layout === 'cards' && selected && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />}
            {layout === 'chips' && selected && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}
